import React from 'react';

interface ConfettiFieldProps {
  /** Number of pieces to scatter */
  density?: number;
  /** Pin pieces inside this absolutely-positioned area */
  className?: string;
  /** Disable subtle floating animation (e.g. for static screenshots) */
  static?: boolean;
}

const COLORS = [
  'fill-confetti-pink',
  'fill-confetti-blue',
  'fill-confetti-lime',
  'fill-confetti-yellow',
  'fill-confetti-coral',
  'fill-confetti-purple',
];

const SHAPES = ['rect', 'circle', 'squiggle'] as const;

/**
 * A decorative field of static-but-floating confetti pieces.
 * Uses a deterministic seed so SSR/CSR match.
 */
export default function ConfettiField({
  density = 28,
  className = '',
  static: isStatic = false,
}: ConfettiFieldProps) {
  // Deterministic pseudo-random based on index
  const pieces = React.useMemo(() => {
    const arr: Array<{
      x: number;
      y: number;
      rot: number;
      size: number;
      color: string;
      shape: (typeof SHAPES)[number];
      animClass: string;
      delay: number;
    }> = [];
    for (let i = 0; i < density; i++) {
      // Simple hash-ish prng
      const r = (n: number) => {
        const s = Math.sin(i * 9301 + n * 49297) * 233280;
        return s - Math.floor(s);
      };
      arr.push({
        x: r(1) * 100,
        y: r(2) * 100,
        rot: r(3) * 360,
        size: 6 + r(4) * 10,
        color: COLORS[Math.floor(r(5) * COLORS.length)],
        shape: SHAPES[Math.floor(r(6) * SHAPES.length)],
        animClass: ['animate-float-slow', 'animate-float-mid', 'animate-float-fast'][
          Math.floor(r(7) * 3)
        ],
        delay: r(8) * 4,
      });
    }
    return arr;
  }, [density]);

  return (
    <div
      aria-hidden="true"
      className={`pointer-events-none absolute inset-0 overflow-hidden ${className}`}
    >
      {pieces.map((p, i) => (
        <span
          key={i}
          className={`absolute ${isStatic ? '' : p.animClass}`}
          style={{
            left: `${p.x}%`,
            top: `${p.y}%`,
            animationDelay: `${p.delay}s`,
            transform: `rotate(${p.rot}deg)`,
          }}
        >
          {p.shape === 'rect' && (
            <svg width={p.size} height={p.size * 0.45} viewBox="0 0 10 4">
              <rect width="10" height="4" rx="1" className={p.color} />
            </svg>
          )}
          {p.shape === 'circle' && (
            <svg width={p.size * 0.6} height={p.size * 0.6} viewBox="0 0 10 10">
              <circle cx="5" cy="5" r="5" className={p.color} />
            </svg>
          )}
          {p.shape === 'squiggle' && (
            <svg width={p.size} height={p.size * 0.5} viewBox="0 0 20 10" fill="none">
              <path
                d="M1 5 Q 5 1, 10 5 T 19 5"
                strokeWidth="2"
                strokeLinecap="round"
                fill="none"
                className={p.color.replace('fill-', 'stroke-')}
              />
            </svg>
          )}
        </span>
      ))}
    </div>
  );
}
